import { Button, Dialog, Div, Input, Pagination, Skeleton, Space, Table, Td, Th, Tr } from '@dnb/eufemia';
import {
  edit as EditIcon,
  trash as TrashIcon,
  trash_medium as TrashMediumIcon,
  view as ViewIcon,
} from '@dnb/eufemia/icons';
import classnames from 'classnames';
import classNames from 'classnames';
import { type JSX, type ReactNode, useEffect, useState } from 'react';

import style from './index.module.css';

export interface Column<T> {
  header: string;
  attribute: Extract<keyof T, string>;
  render?: (value: T) => ReactNode;
}

export interface DataTableProps<T> {
  data: T[];
  onShow?(row: T): void;
  onDelete?(row: T): void;
  onEdit?(row: T): void;
  columns: Column<T>[];
  defaultSortKey: keyof T;
  reverseSort?: boolean;
  skeleton?: boolean;
  filterBy?: Array<keyof T>;
  rowsPerPage?: number;
  stateKey?: string;
  barContent?: JSX.Element;
}

function toString(value?: unknown): string {
  switch (typeof value) {
    case 'string': {
      return value;
    }
    case 'object': {
      return Array.isArray(value) ? value.map(toString).join(', ') : (value?.toString() ?? '');
    }
    case 'number': {
      return value?.toString() ?? '';
    }
    default: {
      return '';
    }
  }
}

export default function DataTable<T>({
  data,
  columns,
  defaultSortKey,
  reverseSort = false,
  onShow,
  onDelete,
  onEdit,
  skeleton = false,
  filterBy,
  rowsPerPage = 15,
  barContent,
}: DataTableProps<T>): JSX.Element {
  const [page, setPage] = useState(1);
  const [query, setQuery] = useState<string>('');
  const [sortKey, setSortKey] = useState<keyof T>(defaultSortKey);
  const [isSortReversed, setIsSortReversed] = useState<boolean>(reverseSort);

  const processedData = (() => {
    const copy = data.filter((row) => {
      if (!filterBy || !query) {
        return true;
      }

      const lowerCasedQuery = query.toLowerCase();

      for (const prop of filterBy) {
        if (toString(row[prop]).toLowerCase().includes(lowerCasedQuery)) {
          return true;
        }
      }

      return false;
    });

    if (sortKey) {
      copy.sort((a, b) => {
        if (typeof a[sortKey] === 'number' || a[sortKey] instanceof Date) {
          return a[sortKey] < b[sortKey] ? -1 : 1;
        } else {
          return toString(a[sortKey]).localeCompare(toString(b[sortKey]));
        }
      });

      if (isSortReversed) {
        copy.reverse();
      }
    }

    return copy;
  })();

  useEffect(() => {
    // Reset the page to 1 only when filtering, sorting, or querying
    if (query || sortKey || isSortReversed || filterBy) {
      setPage(1);
    }
  }, [query, sortKey, isSortReversed, filterBy]);

  const sortBy = (attribute: keyof T) => {
    if (sortKey === attribute) {
      setIsSortReversed(!isSortReversed);
    } else {
      setSortKey(attribute);
      setIsSortReversed(false);
    }
  };

  const numberOfPages = Math.ceil(processedData.length / rowsPerPage);
  const currentPageData = processedData.slice((page - 1) * rowsPerPage, page * rowsPerPage);

  const hasActions = !!(onShow || onEdit || onDelete);
  const numberOfColumns = hasActions ? columns.length + 1 : columns.length;

  const rows = currentPageData.map((row, idx) => (
    <tr key={idx}>
      {columns.map((col) => (
        <td key={col.attribute}>
          {/*@ts-ignore row[col.attribute] could be something that React is unable to render*/}
          {col.render ? col.render(row) : row[col.attribute]}
        </td>
      ))}
      {hasActions && (
        <td className={classnames('dnb-table--right', style['ActionsColumn'])}>
          {onShow && <Button icon={ViewIcon} onClick={() => onShow(row)} right="x-small" size="medium" title="Show" />}
          {onEdit && <Button icon={EditIcon} onClick={() => onEdit(row)} right="x-small" size="medium" title="Edit" />}
          {onDelete && (
            <Dialog
              confirmText="Delete"
              confirmType="warning"
              declineText="Cancel"
              description="This action cannot be undone."
              icon={TrashMediumIcon}
              onConfirm={({ close }: { close: () => void }) => {
                close();
                onDelete(row);
              }}
              title="Are you sure you want to delete this?"
              triggerAttributes={{ icon: TrashIcon }}
              variant="confirmation"
            />
          )}
        </td>
      )}
    </tr>
  ));

  return (
    <Skeleton show={skeleton}>
      <Space bottom="small" className={style['Bar']}>
        {barContent}
        {filterBy && (
          <Input
            icon="loupe"
            onChange={({ value }: { value: string }) => setQuery(value)}
            placeholder="Search"
            showClearButton
            value={query}
          />
        )}
      </Space>
      <Pagination
        className={numberOfPages < 2 ? style['HidePagination'] : ''}
        currentPage={page}
        onChange={({ pageNumber }) => setPage(pageNumber)}
        pageCount={numberOfPages}
      >
        <Div className={style['TableWrapper']}>
          <Table>
            <thead>
              <tr>
                {columns.map((col) => (
                  <th
                    className={classNames({
                      'dnb-table--sortable': true,
                      'dnb-table--active': sortKey === col.attribute,
                      'dnb-table--reversed': sortKey === col.attribute && isSortReversed,
                    })}
                    key={col.attribute}
                    scope="col"
                  >
                    <Button
                      icon="arrow-down"
                      onClick={() => sortBy(col.attribute)}
                      text={col.header}
                      title="Sort table row"
                      variant="tertiary"
                      wrap
                    />
                  </th>
                ))}
                {hasActions && (
                  <Th className="dnb-table--right" scope="col">
                    Actions
                  </Th>
                )}
              </tr>
            </thead>
            <tbody>
              {rows.length > 0 ? (
                rows
              ) : (
                <Tr className="dnb-table--small">
                  <Td colSpan={numberOfColumns}>
                    <i>No matching data</i>
                  </Td>
                </Tr>
              )}
            </tbody>
          </Table>
        </Div>
      </Pagination>
    </Skeleton>
  );
}
